import { Color, Intersection, Matrix4, Object3D, Raycaster, Vector2, Vector3 } from 'three';

import { isShownBelow } from '../../../utils/three/visibility';
import { AssetManager } from '../../asset-manager';
import { SkyviewCamera } from '../../cameras/types';
import { Conic } from '../../elements/design/design-node/utils/conic';
import {
  isStyledLines,
  StyledLines,
  StyledLinesIntersection,
} from '../../elements/styled-lines/styled-lines';
import { readItem, StyledLinesData } from '../../elements/styled-lines/styled-lines-data';
import { toVector2 } from '../../utilities/math-helpers';
import { Pick } from '../../utilities/pick';
import { PickingLine } from '../picking-line';
import { getAssetLine } from './asset-line';
import { getConicCursorVertices } from './context-menu/design-tool/utils/curve';
import { CrossingLine, findClosestCrossing, toCrossingLine } from './line-crossing';
import { getArcCenter, getConnectedCurves, getSnapStylePoint, SnapStyle } from './snap-style';

/**
 * A point near the cursor that a snap style targets.
 */
export type SnapCandidate = {
  style: SnapStyle;
  point: Vector3;
  /** How far the point is from the cursor on screen, in pixels. */
  pixels: number;
  /** The curves that a center is the center of, highlighted to show which curve it belongs to. */
  curves?: Conic[];
};

/**
 * Chooses the point to snap to among the points that the snap styles target:
 * 1. A center within maxPixels of the cursor, as the cursor is moved from the curve to its center.
 * 2. The point closest to the cursor within maxPixels, e.g. a corner or a middle.
 * 3. Without free snapping, the closest point at any distance, as nothing else is snapped to.
 *
 * With free snapping, the hovered position is snapped to when no point is within maxPixels.
 */
export function chooseSnapCandidate<T extends SnapCandidate>(
  candidates: T[],
  free: boolean,
  maxPixels: number,
): T | undefined {
  const closest = (list: T[]) =>
    list.reduce<T | undefined>((a, b) => (!a || b.pixels < a.pixels ? b : a), undefined);
  const near = candidates.filter((x) => x.pixels <= maxPixels);

  return (
    closest(near.filter((x) => x.style === 'center')) ??
    closest(near) ??
    (free ? undefined : closest(candidates))
  );
}

/**
 * Gets the cursor on screen, from the ray through it, as Pick measures the distance of the assets
 * from the cursor.
 */
export const getRayCursor = (camera: SkyviewCamera, rayCaster: Raycaster) =>
  toVector2(camera.getScreenPosition(rayCaster.ray.origin.clone().add(rayCaster.ray.direction)));

/** The number of lines closest to the cursor that the intersection snap style crosses. */
export const maxCrossingLines = 12;

export type AssetCrossingLine = CrossingLine & { pixels: number; key: string };

/**
 * Gets the lines of the assets within maxPixels of the cursor, for finding where they cross.
 */
export function getAssetCrossingLines(
  camera: SkyviewCamera,
  rayCaster: Raycaster,
  predicate: (asset: Object3D) => boolean,
  maxPixels: number,
): AssetCrossingLine[] {
  const lines = new Map<string, AssetCrossingLine>();

  for (const intersection of Pick.instance.assetIntersections(camera, rayCaster, predicate)) {
    const line = getAssetLine(intersection);
    const item = (intersection as Partial<StyledLinesIntersection>).itemIndex;
    const key = `${intersection.object.uuid}:${item ?? intersection.index}`;
    const pixels = intersection.distanceToRay ?? Infinity;

    if (line && pixels <= maxPixels && !lines.has(key)) {
      lines.set(key, { ...toCrossingLine(line), pixels, key });
    }
  }

  return [...lines.values()];
}

/**
 * Gets where two lines of the assets near the cursor cross, for the intersection snap style.
 */
function findAssetCrossing(
  camera: SkyviewCamera,
  rayCaster: Raycaster,
  predicate: (asset: Object3D) => boolean,
  maxPixels: number,
): SnapCandidate | undefined {
  const cursor = getRayCursor(camera, rayCaster);
  // The lines closest to the cursor, since every pair of them is crossed.
  const lines = getAssetCrossingLines(camera, rayCaster, predicate, maxPixels)
    .sort((a, b) => a.pixels - b.pixels)
    .slice(0, maxCrossingLines);
  const crossing = findClosestCrossing(
    lines,
    (p) => camera.getScreenPosition(p),
    cursor,
    maxPixels,
  );

  return (
    crossing && {
      style: 'intersection',
      point: crossing.point,
      pixels: camera.getScreenPosition(crossing.point).distanceTo(cursor),
    }
  );
}

/**
 * The center of curves of StyledLines, in its local coordinates, and the items that are curves
 * around it, e.g. the pieces of a circle.
 */
type CurveCenter = { center: Vector3; items: number[] };

const curveCenters = new WeakMap<StyledLinesData, CurveCenter[]>();
const identity = new Matrix4();

/**
 * Gets the centers of the curves of the lines, computed once for its data.
 */
function getCurveCenters(lines: StyledLines): CurveCenter[] {
  const cached = curveCenters.get(lines.data);

  if (cached) {
    return cached;
  }

  const centers = new Map<string, CurveCenter>();

  for (let i = 0; i < lines.data.count; i++) {
    const item = readItem(lines.data, i);

    if (item.kind !== 'curve') {
      continue;
    }

    const conic = { start: item.start, control: item.control, end: item.end, weight: item.weight };
    const center = getArcCenter(conic, getConnectedCurves(lines, i, identity));

    if (!center) {
      continue;
    }

    // The pieces of a circle have the same center, and are highlighted together.
    const key = center
      .toArray()
      .map((x) => x.toFixed(4))
      .join();
    const existing = centers.get(key);

    if (existing) {
      existing.items.push(i);
    } else {
      centers.set(key, { center, items: [i] });
    }
  }

  const result = [...centers.values()];
  curveCenters.set(lines.data, result);
  return result;
}

/**
 * Gets an item of the lines that is a curve, in world coordinates.
 */
function getWorldConic(lines: StyledLines, index: number): Conic {
  const item = readItem(lines.data, index);

  return {
    start: item.start.applyMatrix4(lines.matrixWorld),
    control: item.control.applyMatrix4(lines.matrixWorld),
    end: item.end.applyMatrix4(lines.matrixWorld),
    weight: item.weight,
  };
}

/**
 * Finds the centers of the curves of the shown assets within maxPixels of the cursor, e.g. of
 * circles and arcs, so that a center is snapped to without hovering its curve.
 */
function findAssetCurveCenters(
  camera: SkyviewCamera,
  cursor: Vector2,
  predicate: (asset: Object3D) => boolean,
  maxPixels: number,
): SnapCandidate[] {
  const frustum = camera.getFrustum();
  const candidates: SnapCandidate[] = [];

  for (const asset of AssetManager.instance.children.filter(predicate)) {
    if (!isShownBelow(asset)) {
      continue;
    }

    asset.traverseVisible((object) => {
      if (!isStyledLines(object)) {
        return;
      }

      const hiddenLayers = object.layerObjects.map((layer) => !isShownBelow(layer, object.parent));

      for (const { center, items } of getCurveCenters(object)) {
        const shown = items.filter((i) => !hiddenLayers[object.data.layers[i]]);
        const point = center.clone().applyMatrix4(object.matrixWorld);

        if (shown.length === 0 || !frustum.containsPoint(point)) {
          continue;
        }

        const pixels = camera.getScreenPosition(point).distanceTo(cursor);

        if (pixels <= maxPixels) {
          const curves = shown.map((i) => getWorldConic(object, i));
          candidates.push({ style: 'center', point, pixels, curves });
        }
      }
    });
  }

  return candidates;
}

/**
 * Gets the hovered curve of an asset and the curves it continues into, e.g. the other pieces of a
 * circle, in world coordinates.
 */
function getHoveredCurves(intersection: Intersection): Conic[] | undefined {
  const { object } = intersection;

  if (!isStyledLines(object)) {
    return undefined;
  }

  const { item, itemIndex } = intersection as StyledLinesIntersection;
  return item.kind === 'curve'
    ? [getWorldConic(object, itemIndex), ...getConnectedCurves(object, itemIndex)]
    : undefined;
}

/**
 * Gets the points of the assets that the snap styles target near the cursor: of the hovered
 * geometry, the centers of curves near the cursor, and where lines near it cross.
 */
export function findAssetSnapCandidates(
  camera: SkyviewCamera,
  rayCaster: Raycaster,
  styles: SnapStyle[],
  intersection: Intersection | undefined,
  predicate: (asset: Object3D) => boolean,
  maxPixels: number,
): SnapCandidate[] {
  const cursor = getRayCursor(camera, rayCaster);
  const candidates: SnapCandidate[] = [];

  for (const style of styles) {
    if (style === 'intersection') {
      const crossing = findAssetCrossing(camera, rayCaster, predicate, maxPixels);
      candidates.push(...(crossing ? [crossing] : []));
      continue;
    }

    const point = intersection && getSnapStylePoint(intersection, style);

    if (point) {
      const pixels = camera.getScreenPosition(point).distanceTo(cursor);
      const curves = style === 'center' ? getHoveredCurves(intersection) : undefined;
      candidates.push({ style, point, pixels, curves });
    }

    if (style === 'center') {
      candidates.push(...findAssetCurveCenters(camera, cursor, predicate, maxPixels));
    }
  }

  return candidates;
}

/**
 * Highlights the curves whose center is snapped to, so that it's seen which curve it belongs to.
 */
export class CenterCurveHighlight extends PickingLine {
  constructor() {
    super(new Color(0, 1, 0));
    this.visible = false;
  }

  show(curves: Conic[] | undefined) {
    this.visible = (curves?.length ?? 0) > 0;

    if (curves && this.visible) {
      this.setVertices(getConicCursorVertices(curves));
    }
  }
}
